import React from "react";
import { useNavigate } from "react-router-dom";
import { KPI_DETAIL, safeKpiTrend } from "../lib/dataKpi";
import { Tile, Badge, StatusDot } from "./Primitives";
import { formatPeso, pct, cn } from "../lib/utils";
import { useCopilot } from "../lib/copilot.jsx";
import { useRole } from "../lib/role.jsx";
import { useChartColors } from "../lib/chartColors";
import {
  AreaChart, Area, Line, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { ArrowLeft, Sparkles, MessageSquare, Lightbulb, ExternalLink } from "lucide-react";

function formatKPI(v, format) {
  if (v === undefined || v === null) return "—";
  if (format === "peso") return formatPeso(v, { compact: true });
  if (format === "pct" || format === "share") return pct(v);
  if (format === "x") return `${(+v).toFixed(1)}×`;
  if (typeof v === "number") return v.toLocaleString();
  return v;
}

const RELATED_LABEL_MAP = {
  "/branches": "Branch Performance",
  "/inventory": "Inventory Intelligence",
  "/procurement": "Procurement & Supply",
  "/omnichannel": "Omnichannel Sales",
  "/merchandising": "Merchandising",
  "/forecast": "Forecast / Prediction Models",
  "/financial": "Financial",
  "/operations": "Daily Operations",
  "/admin/pipeline": "Live Data Pipeline",
};

export default function KpiDrilldown({ kpi, onBack }) {
  const navigate = useNavigate();
  const { askCopilot } = useCopilot();
  const { canAccess } = useRole();
  const C = useChartColors();
  const detail = KPI_DETAIL[kpi.name];
  const trend = detail?.trend || safeKpiTrend(kpi.name, kpi.actual);
  const formatV = (v) => formatKPI(v, kpi.format);

  // Filter cross-module links by role access so pilot users don't hit AccessRestricted.
  const relatedLinks = (detail?.related || []).filter((p) => canAccess(p));

  const first = trend[0]?.value;
  const last = trend[trend.length - 1]?.value;
  const trendDelta = first ? (last - first) / first : 0;

  const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

  function TT({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
        <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2 font-mono-tabular">
            <span className="w-2 h-2" style={{ background: p.color }} />
            <span className="text-muted">{p.name}</span>
            <span className="ml-auto font-semibold">{formatV(p.value)}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={onBack}
            data-testid="kpi-back"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted hover:text-primary mb-2"
          >
            <ArrowLeft size={12} /> Back to KPI Framework
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">{kpi.category} · KPI Drill-down</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{kpi.name}</h1>
          <p className="text-muted text-sm mt-1.5 max-w-2xl">{kpi.definition}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge status={kpi.status}>{kpi.status}</Badge>
          {detail && <Badge status="info">AI conf. {(detail.confidence * 100).toFixed(0)}%</Badge>}
        </div>
      </div>

      {/* Summary strip */}
      <div className="tile grid grid-cols-2 md:grid-cols-5 divide-x divide-border" data-testid="kpi-summary">
        <Stat label="Actual" value={formatV(kpi.actual)} status={kpi.status} />
        <Stat label="Target" value={kpi.target} />
        <Stat label="24M Δ" value={`${trendDelta >= 0 ? "+" : ""}${(trendDelta * 100).toFixed(1)}%`} status={trendDelta >= 0 ? "success" : "critical"} />
        <Stat label="Period Start" value={formatV(first)} />
        <Stat label="Period End" value={formatV(last)} />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* 24-month trend chart */}
        <Tile
          className="col-span-12"
          testid="kpi-trend-chart"
          title="24-Month Historical Trend"
          action={
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-primary" />Actual</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 border-t border-dashed border-success" />Target</span>
            </div>
          }
        >
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={trend} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="kpi-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.primary} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={C.primary} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
                <XAxis dataKey="month" tick={tickStyle} stroke={C.borderStrong} interval={1} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={(v) => formatV(v)} />
                <Tooltip content={<TT />} cursor={{ stroke: C.borderStrong, strokeDasharray: "3 3" }} />
                <Area type="monotone" dataKey="value" name="Actual" stroke={C.primary} strokeWidth={2.4} fill="url(#kpi-area)" isAnimationActive={false} />
                <Line type="monotone" dataKey="target" name="Target" stroke={C.success} strokeWidth={1.4} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* AI Explanation */}
        <Tile
          className="col-span-12 lg:col-span-7"
          testid="kpi-ai-explanation"
          title="AI Explanation"
          action={detail && <Badge status="info">Confidence {(detail.confidence * 100).toFixed(0)}%</Badge>}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-fg/95 leading-relaxed">
                {detail?.aiExplanation || `${kpi.name} is currently ${formatV(kpi.actual)}, ${kpi.status === "success" ? "above" : kpi.status === "warning" ? "near" : "below"} the target of ${kpi.target}. Detailed AI explanation not yet generated for this KPI — request via Copilot for full diagnostic.`}
              </p>
              {detail && (
                <button
                  data-testid="ask-copilot-kpi"
                  onClick={() =>
                    askCopilot(
                      `As CEO, deep-dive on KPI "${kpi.name}" (category: ${kpi.category}). Current actual ${formatV(kpi.actual)}, target ${kpi.target}, status ${kpi.status}. 24-month trend: from ${formatV(first)} to ${formatV(last)} (${trendDelta >= 0 ? "+" : ""}${(trendDelta * 100).toFixed(1)}%). AI assessment: ${detail.aiExplanation} Top drivers: ${detail.drivers.map((d) => `${d.label} (weight ${(d.weight * 100).toFixed(0)}%, delta ${d.delta >= 0 ? "+" : ""}${d.delta})`).join("; ")}. Recommended Next-Best Actions: ${detail.nba.map((n) => `"${n.title}" (impact ${formatPeso(n.impact, { compact: true })}, owner ${n.owner}, urgency ${n.urgency})`).join("; ")}. Walk me through: (1) the real root cause, (2) which of the recommended actions to greenlight now vs hold, (3) what second-order metrics will move, (4) what risks I'm not seeing.`
                    )
                  }
                  className="mt-3 inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                >
                  <MessageSquare size={11} /> Ask Copilot to deep-dive →
                </button>
              )}
            </div>
          </div>
        </Tile>

        {/* Drivers breakdown */}
        <Tile className="col-span-12 lg:col-span-5" testid="kpi-drivers" title="Driver Decomposition">
          {detail?.drivers ? (
            <div className="space-y-2.5">
              {detail.drivers.map((d, i) => (
                <div key={i} data-testid={`driver-${i}`}>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="font-medium">{d.label}</span>
                    <span className={cn("font-mono-tabular font-semibold", d.delta >= 0 ? "text-success" : "text-critical")}>
                      {d.delta >= 0 ? "+" : ""}
                      {typeof d.delta === "number" && Math.abs(d.delta) < 1 ? `${(d.delta * 100).toFixed(1)}%` : d.delta.toFixed(2)}
                    </span>
                  </div>
                  <div className="h-2 bg-surface-2 rounded-sm overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${d.weight * 100}%`, opacity: 0.4 + d.weight * 0.6 }} />
                  </div>
                  <div className="text-[9px] text-subtle font-mono-tabular mt-0.5">Weight {(d.weight * 100).toFixed(0)}%</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[12px] text-muted italic">Driver decomposition not yet available for this KPI.</div>
          )}
        </Tile>

        {/* Next Best Actions */}
        <Tile
          className="col-span-12"
          testid="kpi-nba"
          title="AI Recommendations · Next Best Actions"
          action={detail?.nba && <Badge status="success">Total potential impact {formatPeso(detail.nba.reduce((s, n) => s + (n.impact || 0), 0), { compact: true })}</Badge>}
        >
          {detail?.nba ? (
            <div className="space-y-2">
              {detail.nba.map((nba, i) => (
                <div key={i} data-testid={`nba-${i}`} className="p-3 border border-border rounded-sm bg-surface-2/40 hover:bg-surface-2 transition-colors flex items-start gap-3">
                  <div
                    className={cn(
                      "w-7 h-7 rounded-sm flex items-center justify-center shrink-0 mt-0.5",
                      nba.urgency === "high" ? "bg-critical/15 text-critical" : nba.urgency === "medium" ? "bg-warning/15 text-warning" : "bg-info/15 text-info"
                    )}
                  >
                    <Lightbulb size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge status={nba.urgency === "high" ? "critical" : nba.urgency === "medium" ? "warning" : "info"}>{nba.urgency}</Badge>
                      <span className="text-[10px] text-muted">Owner: {nba.owner}</span>
                    </div>
                    <div className="text-sm font-medium">{nba.title}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-tabular text-sm font-semibold text-primary">{nba.impact > 0 ? formatPeso(nba.impact, { compact: true }) : "—"}</div>
                    <div className="text-[10px] text-muted uppercase tracking-widest">Impact</div>
                  </div>
                  <button className="px-3 py-1.5 text-[10px] border border-success/40 text-success hover:bg-success/10 rounded-sm shrink-0" data-testid={`nba-approve-${i}`}>
                    Approve
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[12px] text-muted italic">Next Best Actions not yet generated for this KPI. Use Ask Copilot to request a custom playbook.</div>
          )}
        </Tile>

        {/* Related modules */}
        {detail?.related && relatedLinks.length > 0 && (
          <Tile className="col-span-12" title="Related Modules">
            <div className="flex flex-wrap gap-2">
              {relatedLinks.map((path, i) => (
                <button
                  key={i}
                  onClick={() => navigate(path)}
                  data-testid={`related-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-sm text-xs hover:bg-surface-2 hover:border-primary transition-colors"
                >
                  {RELATED_LABEL_MAP[path] || path}
                  <ExternalLink size={11} />
                </button>
              ))}
            </div>
          </Tile>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-xl mt-1.5">{value}</div>
    </div>
  );
}
